// TelcoTrack — FE typy Modul Sklad (mirror backend warehouse.types + integration DTOs)

export type GoodsIssueStatus =
  | 'Draft'
  | 'Reserving'
  | 'Reserved'
  | 'Processing'
  | 'Issued'
  | 'Canceled';

export type WarehouseTransferStatus =
  | 'Draft'
  | 'Reserving'
  | 'Reserved'
  | 'Processing'
  | 'Transferred'
  | 'Canceled';

export const GOODS_ISSUE_STATUS_FLOW: readonly GoodsIssueStatus[] = [
  'Draft',
  'Reserving',
  'Reserved',
  'Processing',
  'Issued',
] as const;

export const WAREHOUSE_TRANSFER_STATUS_FLOW: readonly WarehouseTransferStatus[] = [
  'Draft',
  'Reserving',
  'Reserved',
  'Processing',
  'Transferred',
] as const;

export interface WarehouseStatusInfo {
  enabled: boolean;
  configured: boolean;
  baseUrlHost: string | null;
  defaultWarehouseId: string | null;
  authMode: string | null;
  productCodeField: string | null;
  deliveryNotesEnabled?: boolean;
  ocrEnabled?: boolean;
  aresEnabled?: boolean;
  ocrProvider?: string;
}

export interface WarehouseListResult {
  list: WarehouseEntity[];
  total?: number;
  expectedTotal?: number;
  aclIncomplete?: boolean;
  warning?: string | null;
}

export interface WarehouseEntity {
  id: string;
  name: string;
  type?: 'Simple' | 'Positional' | null;
  isVirtual?: boolean;
  variantsEnabled?: boolean;
  autoPurchaseEnabled?: boolean;
  /** TelcoTrack user linked via users.warehouse_id. */
  linkedUserId?: string | null;
  linkedUserEmail?: string | null;
  linkedUserName?: string | null;
  /** Specifikace skladu (`warehouse_profile`) — IČO / adresa na dokladech. */
  address?: string | null;
  responsibleCompanyName?: string | null;
  responsibleCompanyIco?: string | null;
  responsibleCompanyAddress?: string | null;
}

export interface GoodsReceipt {
  id: string;
  warehouseId: string;
  status: string;
  name?: string | null;
  itemsIds?: string[];
  referenceIdentifier?: string | null;
  totalReceivedPrice?: number | null;
}

/** TT doklad příjemky (warehouse_goods_receipt). */
export interface WarehouseGoodsReceiptDocument {
  id: string;
  warehouseId: string;
  status: string;
  description: string | null;
  referenceIdentifier: string | null;
  supplierId: string | null;
  supplierName: string | null;
  totalReceivedPrice: number | null;
  currency: string;
  createdBy: string | null;
  receivedAt: string | null;
  createdAt: string;
  /** true = nahraný OCR DL je uložen; Stáhnout vrací zdroj, ne generovaný protokol. */
  hasSourceDocument?: boolean;
  sourceDocumentFilename?: string | null;
  sourceDocumentContentType?: string | null;
  items: Array<{
    id: string;
    lineNo: number;
    sapCode: string;
    productId: string | null;
    productName: string | null;
    quantity: number;
    unit: string | null;
    unitPrice: number | null;
    lineTotal: number | null;
    positionId: string | null;
    warehouseItemId: string | null;
    batchNumber: string | null;
  }>;
}

/** Uživatelská příloha příjemky (ne OCR zdrojový DL). */
export interface WarehouseGoodsReceiptAttachment {
  id: string;
  receiptId: string;
  originalName: string;
  contentType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
}

export interface WarehouseItem {
  id: string;
  productId: string;
  positionId?: string | null;
  quantity: number;
  quantityReserved: number;
  quantityAvailable: number;
  productSerialId?: string | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  productCode?: string | null;
  productName?: string | null;
  /** Jednotková cena (příjemka, jinak ceník overlay); null = nedostupné. */
  unitPrice?: number | null;
  /** Cena z příjemky na lotu — bez overlay živého ceníku. */
  receiptUnitPrice?: number | null;
  unit?: string | null;
  positionName?: string | null;
  /** Označení fyzického kusu (smotek / role). */
  label?: string | null;
  /** Sklad, na kterém kus leží. */
  warehouseId?: string | null;
}

export interface WarehouseStockSnapshot {
  warehouseId: string;
  items: WarehouseItem[];
  fetchedAt: string;
}

/** TT1 sken resolve — konstantní produkt+sklad; kusy se volí po skenu. */
export interface WarehouseScanResolveResult {
  code: string;
  warehouseId: string;
  warehouseName: string;
  warehouseGroup?: 'branches' | 'workers';
  sapCode: string;
  productId: string | null;
  productName: string;
  unit: string | null;
  pieceLength: boolean;
  quantityAvailable: number;
  warehouseItemId: string | null;
  pieces: Array<{
    warehouseItemId: string;
    index: number;
    quantityAvailable: number;
    /** AutoCRM field — not shown in materials UI. */
    quantityReserved: number;
    unit: string | null;
    productSerialId: string | null;
    batchNumber: string | null;
    positionName: string | null;
    /** Označení fyzického kusu (smotek). */
    label?: string | null;
    /** Always constant TT1/{warehouseId}/{sapCode}. */
    scanCode: string;
  }>;
  requiresPieceSelection: boolean;
  canTake?: boolean;
  takeDeniedReason?: string | null;
}

export interface SapStockAvailability {
  sapCode: string;
  productId: string | null;
  quantityAvailable: number;
  quantityReserved: number;
  quantity: number;
  matched: boolean;
  requestedQuantity?: number;
  sufficient?: boolean;
}

export interface ObjectMaterialIssuePreview {
  objectId: string;
  warehouseId: string;
  lines: SapStockAvailability[];
  productIds: string[];
  allMatched: boolean;
  allSufficient: boolean;
}

export interface GoodsIssue {
  id: string;
  warehouseId: string;
  status: GoodsIssueStatus;
  name?: string | null;
  selectedItemsIds?: string[];
  itemsIds?: string[];
  assignedUserId?: string | null;
  totalIssuedPrice?: number | null;
  description?: string | null;
  items?: WarehouseTransferLineItem[];
  createdAt?: string | null;
  createdByName?: string | null;
  performedBy?: string | null;
  performedAt?: string | null;
  totalPrice?: number | null;
  fromName?: string | null;
  fromIco?: string | null;
  fromAddress?: string | null;
  toName?: string | null;
  toIco?: string | null;
  toAddress?: string | null;
  /** Objekty, kde byl materiál z této výdejky použit. */
  linkedObjectIds?: string[];
}

export interface WarehouseTransferLineItem {
  sapCode?: string | null;
  productId?: string | null;
  productName?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice?: number | null;
  warehouseItemId?: string | null;
  batchNumber?: string | null;
  objectId?: string | null;
  objectIds?: string[] | null;
}

export interface WarehouseTransfer {
  id: string;
  name: string;
  warehouseFromId: string;
  warehouseToId: string;
  status: WarehouseTransferStatus;
  itemsForReservationIds?: string[];
  itemsIds?: string[];
  description?: string | null;
  items?: WarehouseTransferLineItem[];
  createdAt?: string | null;
  /** AutoCRM createdByName — UI must not use for „Kdo“ (often API user telcotrack). */
  createdByName?: string | null;
  /** TelcoTrack actor email from object_history (list + detail). */
  performedBy?: string | null;
  performedAt?: string | null;
  /** Součet qty × unitPrice — BE enrichment z katalogu. */
  totalPrice?: number | null;
  /** Osobní ↔ osobní: protistrana musí potvrdit přijetí / výdej. */
  pendingConfirmation?: boolean;
  confirmationKind?: 'prijem' | 'vydej' | null;
  confirmerWarehouseId?: string | null;
  canConfirm?: boolean;
  canReject?: boolean;
}

export interface WarehouseTransferPreview {
  warehouseFromId: string;
  warehouseToId: string;
  lines: SapStockAvailability[];
  productIds: string[];
  allMatched: boolean;
  allSufficient: boolean;
}

export interface WarehousePosition {
  id: string;
  name: string;
  warehouseId: string;
  parentId?: string | null;
  capacity?: number | null;
}

/** Řádek soupisu inventury (jeden fyzický kus). */
export interface InventorySoupisLine {
  sapCode: string;
  productId: string | null;
  productName: string | null;
  unit: string | null;
  systemQuantity: number;
  warehouseItemId?: string | null;
  label?: string | null;
  batchNumber?: string | null;
  /** Metrové kusy pod SAP (označení smotků). */
  pieces?: Array<{
    warehouseItemId: string;
    quantity: number;
    label: string | null;
    batchNumber: string | null;
  }>;
}

export interface InventoryCheckPreview {
  warehouseId: string;
  warehouseName: string;
  fetchedAt: string;
  lines: InventorySoupisLine[];
}

export interface InventoryDifferenceLine {
  sapCode: string | null;
  productId: string;
  productName?: string | null;
  unit?: string | null;
  systemQuantity: number;
  countedQuantity: number;
  difference: number;
  warehouseItemId?: string | null;
  batchNumber?: string | null;
}

export interface InventoryCheckSummary {
  lineCount: number;
  matchCount: number;
  surplusCount: number;
  shortageCount: number;
}

export interface PhysicalInventory {
  id: string;
  name?: string | null;
  warehouseId: string;
  status?: string | null;
  description?: string | null;
  dateStart?: string | null;
}

/** Výsledek POST /inventory-check. */
export interface InventoryCheckResult {
  inventory: PhysicalInventory;
  warehouseId: string;
  warehouseName: string;
  differences: InventoryDifferenceLine[];
  summary: InventoryCheckSummary;
  corrections: {
    applied: boolean;
    added: Array<{ sapCode: string; quantity: number; batchNumber?: string | null }>;
    removed: Array<{ sapCode: string; quantity: number; batchNumber?: string | null }>;
  } | null;
  localId?: string;
}

/** TelcoTrack DB inventura (Draft → Completed/Corrected). */
export interface WarehouseInventoryCheckItem {
  id: string;
  sapCode: string;
  warehouseItemId?: string | null;
  label?: string | null;
  batchNumber?: string | null;
  productId: string | null;
  productName: string | null;
  unit: string | null;
  systemQuantity: number;
  countedQuantity: number;
  differenceQuantity: number;
  positionId: string | null;
  correctionApplied: boolean;
  correctionRef: string | null;
}

export interface WarehouseInventoryCheck {
  id: string;
  warehouseId: string;
  name: string | null;
  description: string | null;
  status: string;
  erpInventoryId: string | null;
  createdBy: string | null;
  completedAt: string | null;
  createdAt: string;
  items: WarehouseInventoryCheckItem[];
  summary: {
    lineCount: number;
    surplusLines: number;
    shortageLines: number;
    matchLines: number;
  };
}
